import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { useRecipes } from '../hooks/use-recipes';
import { useSiteInfo } from '@/SiteInfo/hooks/use-site-info';
import { RecipeCard } from '../components/RecipeCard';
import { RecipeDetailSheet } from '../components/RecipeDetailSheet';
import { RecipeHeroCarousel } from '../components/RecipeHeroCarousel';
import { Recipe } from '../types/recipes';
import { SEO } from '@/components/SEO';
import { IconAlertCircle } from '@tabler/icons-react';

const Recipes = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: recipes, isLoading, error } = useRecipes();
  const { data: siteInfo } = useSiteInfo();
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cuisineFilter, setCuisineFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const RECIPES_PER_PAGE = 24;

  // Handle URL param to open recipe detail sheet
  useEffect(() => {
    const openParam = searchParams.get('open');
    if (openParam && recipes) {
      const recipeId = parseInt(openParam, 10);
      const recipe = recipes.find(r => r.id === recipeId);
      if (recipe) {
        setSelectedRecipe(recipe);
        setDialogOpen(true);
        // Clear the URL param after opening
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, recipes, setSearchParams]);

  const handleRecipeClick = (recipe: Recipe) => {
    setSelectedRecipe(recipe);
    setDialogOpen(true);
  };

  // Filter only available recipes
  const availableRecipes = recipes?.filter(recipe => recipe.RecipeAvailability) || [];

  // Get the 5 most recent recipes for the carousel (already sorted by API)
  const carouselRecipes = availableRecipes.slice(0, 5);

  // Extract unique cuisines and types for filters
  const cuisines = useMemo(() => {
    const uniqueCuisines = new Map<number, string>();
    availableRecipes.forEach(recipe => {
      if (recipe.recipe_cuisine) {
        uniqueCuisines.set(recipe.recipe_cuisine.id, recipe.recipe_cuisine.RecipeCuisine);
      }
    });
    return Array.from(uniqueCuisines.entries()).map(([id, name]) => ({ id, name }));
  }, [availableRecipes]);

  const types = useMemo(() => {
    const uniqueTypes = new Map<number, string>();
    availableRecipes.forEach(recipe => {
      if (recipe.recipe_type) {
        uniqueTypes.set(recipe.recipe_type.id, recipe.recipe_type.RecipeType);
      }
    });
    return Array.from(uniqueTypes.entries()).map(([id, name]) => ({ id, name }));
  }, [availableRecipes]);

  // Filter recipes based on selected cuisine and type
  const filteredRecipes = useMemo(() => {
    return availableRecipes.filter(recipe => {
      if (cuisineFilter !== 'all' && recipe.recipe_cuisine?.RecipeCuisine !== cuisineFilter) {
        return false;
      }
      if (typeFilter !== 'all' && recipe.recipe_type?.RecipeType !== typeFilter) {
        return false;
      }
      return true;
    });
  }, [availableRecipes, cuisineFilter, typeFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [cuisineFilter, typeFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredRecipes.length / RECIPES_PER_PAGE);
  const paginatedRecipes = useMemo(() => {
    const startIndex = (currentPage - 1) * RECIPES_PER_PAGE;
    return filteredRecipes.slice(startIndex, startIndex + RECIPES_PER_PAGE);
  }, [filteredRecipes, currentPage]);

  const siteTitle = siteInfo?.SiteTitle || '';

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Simple and Delicious Recipes | Fresh Ideas Weekly"
        description="Discover delicious recipes, cooking tips, and meal inspiration. Find recipes for every occasion."
      />
      {/* Hero Carousel */}
      {isLoading && (
        <div className="w-full aspect-[2/1] bg-muted animate-pulse" />
      )}
      {!isLoading && carouselRecipes.length > 0 && (
        <RecipeHeroCarousel
          recipes={carouselRecipes}
          onRecipeClick={handleRecipeClick}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            {siteTitle ? `Recipes by ${siteTitle}` : 'All Recipes'}
          </h1>
          <div className="hidden sm:flex items-center gap-4">
            {cuisines.length > 1 && (
              <Select value={cuisineFilter} onValueChange={setCuisineFilter}>
                <SelectTrigger className="w-[180px] bg-neutral-50">
                  <SelectValue placeholder="Filter by cuisine" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-50">
                  <SelectItem value="all" className="text-neutral-500">All Cuisines</SelectItem>
                  {cuisines.map((cuisine) => (
                    <SelectItem key={cuisine.id} value={cuisine.name} className="text-neutral-500">
                      {cuisine.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {types.length > 1 && (
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px] bg-neutral-50">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-50">
                  <SelectItem value="all">All Types</SelectItem>
                  {types.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <IconAlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load recipes. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        {isLoading && (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Card key={i} className="overflow-hidden">
                <Skeleton className="w-full aspect-[16/10]" />
                <div className="p-4 space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <div className="flex gap-2 mt-4">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-6 w-16" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && !error && filteredRecipes.length > 0 && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {paginatedRecipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onClick={() => handleRecipeClick(recipe)}
                />
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Pagination className="mt-8">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>

                  {/* First page */}
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => setCurrentPage(1)}
                      isActive={currentPage === 1}
                      className="cursor-pointer"
                    >
                      1
                    </PaginationLink>
                  </PaginationItem>

                  {/* Ellipsis after first page */}
                  {currentPage > 3 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {/* Pages around current */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => page !== 1 && page !== totalPages && Math.abs(page - currentPage) <= 1)
                    .map(page => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}

                  {/* Ellipsis before last page */}
                  {currentPage < totalPages - 2 && (
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                  )}

                  {/* Last page */}
                  {totalPages > 1 && (
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => setCurrentPage(totalPages)}
                        isActive={currentPage === totalPages}
                        className="cursor-pointer"
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  )}

                  <PaginationItem>
                    <PaginationNext
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        )}

        {!isLoading && !error && availableRecipes.length > 0 && filteredRecipes.length === 0 && (
          <Alert>
            <IconAlertCircle className="h-4 w-4" />
            <AlertTitle>No Matching Recipes</AlertTitle>
            <AlertDescription>
              No recipes match the selected filters. Try adjusting your filters.
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !error && availableRecipes.length === 0 && (
          <Alert>
            <IconAlertCircle className="h-4 w-4" />
            <AlertTitle>No Recipes Found</AlertTitle>
            <AlertDescription>
              No recipes are currently available. Please check back later.
            </AlertDescription>
          </Alert>
        )}

        <RecipeDetailSheet
          recipe={selectedRecipe}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      </div>
    </div>
  );
};

export default Recipes;

